import { useParams } from 'react-router';

type Segments<P extends string> = P extends `${infer Head}/${infer Rest}` ? Head | Segments<Rest> : P;

type RequiredParamNames<P extends string> =
  Segments<P> extends infer S ? (S extends `:${string}?` ? never : S extends `:${infer N}` ? N : never) : never;

type OptionalParamNames<P extends string> =
  Segments<P> extends infer S ? (S extends `:${infer N}?` ? N : never) : never;

export type RouteParams<P extends string> = { [K in RequiredParamNames<P>]: string } & {
  [K in OptionalParamNames<P>]?: string;
};

type AnyRouteParams<P extends string> = P extends string ? RouteParams<P> : never;

const requiredParamNames = (pattern: string) =>
  pattern
    .split('/')
    .filter((segment) => segment.startsWith(':') && !segment.endsWith('?'))
    .map((segment) => segment.slice(1));

export function useRouteParams<P extends string>(...patterns: [P, ...P[]]): AnyRouteParams<P> {
  const params = useParams();
  const matches = patterns.some((pattern) => requiredParamNames(pattern).every((name) => params[name] !== undefined));
  if (!matches && process.env.NODE_ENV === 'development') {
    console.error(`useRouteParams: the current route has no params for ${patterns.join(', ')}`);
  }
  return params as AnyRouteParams<P>;
}
